import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    project: '',
    message: '',
    budget: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const projectTypes = [
    { value: 'web3', label: 'Web3 & Blockchain Development', icon: 'Zap' },
    { value: 'community', label: 'Community Building & Management', icon: 'Users' },
    { value: 'software', label: 'Software Development', icon: 'Code' },
    { value: 'consulting', label: 'Technical Consulting', icon: 'MessageSquare' },
    { value: 'other', label: 'Other', icon: 'HelpCircle' }
  ];

  const budgetRanges = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: 'over-50k', label: 'Over $50,000' },
    { value: 'discuss', label: 'Let\'s discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: '1-month', label: 'Within 1 month' },
    { value: '3-months', label: 'Within 3 months' },
    { value: '6-months', label: 'Within 6 months' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Prepare form data for submission
      const formSubmitData = new FormData();
      
      // Required Web3Forms field - replace with your access key from web3forms.com
      formSubmitData.append('access_key', import.meta.env.VITE_WEB3FORMS_ACCESS_KEY);
      
      // User submitted data
      formSubmitData.append('name', formData.name);
      formSubmitData.append('email', formData.email);
      formSubmitData.append('project_type', formData.project);
      formSubmitData.append('message', formData.message);
      formSubmitData.append('budget', formData.budget);
      formSubmitData.append('timeline', formData.timeline);
      
      // Optional: Subject line for the email
      formSubmitData.append('subject', `New Project Inquiry: ${formData.project}`);
      
      // Hidden field to prevent spam
      formSubmitData.append('botcheck', '');
      
      // Send the form data to Web3Forms API
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formSubmitData
      });

      
      const result = await response.json();
      console.log(result);
      
      if (result.success) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          project: '',
          message: '',
          budget: '',
          timeline: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitError(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitError('Failed to submit the form. Please try again later.');
      console.log(error);
    } finally {
      setIsSubmitting(false);
      
      // Clear success/error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setSubmitError(null);
      }, 5000);
    }
  };

  const contactMethods = [
    {
      icon: 'Mail',
      label: 'Email',
      value: 'nikunjmnavdiya@gmail.com',
      description: 'Best for detailed project discussions',
      color: 'primary',
      link: 'mailto:nikunjmnavdiya@gmail.com'
    },
    {
      icon: 'MessageCircle',
      label: 'Twitter',
      value: '@navdiya_nikunj',
      description: 'Quick questions and updates',
      color: 'success',
      link: 'https://x.com/navdiya_nikunj'
    },
    {
      icon: 'Linkedin',
      label: 'LinkedIn',
      value: 'Nikunj Navdiya',
      description: 'Professional networking',
      color: 'accent',
      link: 'https://www.linkedin.com/in/nikunj-navdiya-024543235/'
    }
  ];

  return (
    <section className="py-20 bg-background" id="contact">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            Let's{' '}
            <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
              Create Together
            </span>
          </h2>
          <p className="font-body text-text-secondary text-lg max-w-3xl mx-auto">
            Ready to bring your vision to life? Whether it's building a community, developing a DApp, or crafting software solutions, I'm here to help.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-surface rounded-2xl p-8 shadow-elevation-2 border border-border"
          >
            <div className="mb-8">
              <h3 className="font-heading text-2xl font-bold text-primary mb-2">
                Start Your Project
              </h3>
              <p className="font-body text-text-secondary">
                Fill out the form below and I'll get back to you within 24 hours.
              </p>
            </div>

            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center space-x-3"
              >
                <Icon name="CheckCircle" size={20} className="text-success" />
                <span className="font-body text-success">
                  Thank you! Your message has been sent successfully.
                </span>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-center space-x-3"
              >
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span className="font-body text-error">
                  {submitError || 'Something went wrong. Please try again.'}
                </span>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Hidden field to prevent spam */}
              <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />

              {/* Name & Email */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body font-medium text-primary mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block font-body font-medium text-primary mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Project Type */}
              <div>
                <label className="block font-body font-medium text-primary mb-2">
                  Project Type *
                </label>
                <select
                  name="project"
                  value={formData.project}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body"
                >
                  <option value="">Select project type</option>
                  {projectTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Budget & Timeline */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-body font-medium text-primary mb-2">
                    Budget Range
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body"
                  >
                    <option value="">Select budget range</option>
                    {budgetRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-body font-medium text-primary mb-2">
                    Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body"
                  >
                    <option value="">Select timeline</option>
                    {timelineOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block font-body font-medium text-primary mb-2">
                  Project Details *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 font-body resize-none md:min-h-96"
                  placeholder="Tell me about your project, goals, and any specific requirements..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-surface px-8 py-4 rounded-lg font-body font-medium hover:bg-secondary transition-all duration-200 transform hover:scale-105 shadow-elevation-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-surface border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={20} />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Methods */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-heading text-2xl font-bold text-primary mb-6">
                Other Ways to Connect
              </h3>
              <div className="space-y-4">
                {contactMethods.map((method, index) => (
                  <motion.div
                    key={method.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-surface border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 bg-${method.color}/10 rounded-lg flex items-center justify-center group-hover:bg-${method.color}/20 transition-colors duration-300`}>
                        <Icon name={method.icon} size={24} className={`text-${method.color}`} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-body font-semibold text-primary mb-1">
                          {method.label}
                        </h4>
                        <p className={`font-body text-${method.color} font-medium mb-2`}>
                          {method.value}
                        </p>
                        <p className="font-caption text-text-secondary text-sm">
                          {method.description}
                        </p>
                      </div>
                      <Icon name="ExternalLink" onClick={() => window.open(method.link, '_blank')} size={16} className="text-text-secondary group-hover:text-primary transition-colors duration-300" />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Availability */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-success/10 border border-success/20 rounded-xl p-6"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
                <h4 className="font-body font-semibold text-success">
                  Currently Available
                </h4>
              </div>
              <p className="font-body text-text-secondary mb-4">
                I'm accepting new projects and would love to hear about yours. Typical response time is within 24 hours.
              </p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-success" />
                  <span className="font-caption text-text-secondary text-sm">
                    Response time: &lt; 24 hours
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="MapPin" size={16} className="text-success" />
                  <span className="font-caption text-text-secondary text-sm">
                    Timezone: PST (UTC-8)
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Globe" size={16} className="text-success" />
                  <span className="font-caption text-text-secondary text-sm">
                    Remote work worldwide
                  </span>
                </div>
              </div>
            </motion.div>

            {/* FAQ */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-surface border border-border rounded-xl p-6"
            >
              <h4 className="font-body font-semibold text-primary mb-4">
                Quick FAQ
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="font-body font-medium text-primary text-sm mb-1">
                    What's your typical project timeline?
                  </p>
                  <p className="font-caption text-text-secondary text-sm">
                    Depends on scope, but most projects range from 2-12 weeks.
                  </p>
                </div>
                <div>
                  <p className="font-body font-medium text-primary text-sm mb-1">
                    Do you work with startups?
                  </p>
                  <p className="font-caption text-text-secondary text-sm">
                    Absolutely! I love helping startups bring their vision to life.
                  </p>
                </div>
                <div>
                  <p className="font-body font-medium text-primary text-sm mb-1">
                    Can you help with ongoing maintenance?
                  </p>
                  <p className="font-caption text-text-secondary text-sm">
                    Yes, I offer maintenance and support packages for all projects.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;